import {
  DischargeSummary,
  IMedicine,
  IPD,
  MaternityDischargeSummary,
  TIpdStatus,
  TIpdCase,
  IpdStats,
  ProgressRecord
} from "../interfaces/IpdInterface";
import { ServiceInterface } from "../interfaces/ServiceInterface";
import { Delete, Get, Patch, Post } from "./apiHelper";
import { NextIpdNumber } from "../hooks/useGetNextIpdNumber";
import { ApiResponse } from "../interfaces/Api";
import { IPDFormState } from "../containers/IPD/IpdAdmission/IpdAdmissionCreateEdit";
import { TWardType } from "../interfaces/WardInterface";

const ipdRoot = "/api/ipd";

export async function getIpds({
  status,
  page,
  pageSize,
  currentCase
}: {
  status: TIpdStatus | string;
  page: number | string;
  pageSize: number | string;
  currentCase?: TIpdCase | string;
}): Promise<ApiResponse<{ total: number; results: Array<IPD> }>> {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  if (currentCase) params.append("currentCase", currentCase);

  const response = await Get(`${ipdRoot}?${params.toString()}`, true);
  return response.data as ApiResponse<{ total: number; results: Array<IPD> }>;
}

export async function getActiveIPDs(): Promise<ApiResponse<Array<IPD>>> {
  const response = await Get(`${ipdRoot}/active`, true);
  return response.data as ApiResponse<Array<IPD>>;
}

export async function postIpd(ipdData: IPDFormState): Promise<ApiResponse<IPD>> {
  const response = await Post(`${ipdRoot}`, ipdData, true);
  return response.data as ApiResponse<IPD>;
}

export async function patchIpd(id: number, ipdData: IPDFormState): Promise<ApiResponse<IPD>> {
  const response = await Patch(`${ipdRoot}/${id}`, ipdData, true);
  return response.data as ApiResponse<IPD>;
}

export async function createDischargeSummary(
  id: number,
  ipdData: DischargeSummary | MaternityDischargeSummary
): Promise<ApiResponse<IPD>> {
  const response = await Patch(`${ipdRoot}/${id}/discharge`, ipdData, true);
  return response.data as ApiResponse<IPD>;
}

export async function getIpdtDetails(id: number): Promise<ApiResponse<IPD>> {
  const response = await Get(`${ipdRoot}/${id}`, true);
  return response.data as ApiResponse<IPD>;
}

export async function createProgressRecord(
  ipdId: number,
  progressRecordData: Partial<ProgressRecord>
): Promise<ApiResponse<IPD>> {
  const response = await Patch(`${ipdRoot}/${ipdId}/progressRecord`, progressRecordData, true);
  return response.data as ApiResponse<IPD>;
}

// TODO: remove the getIpdStatsForToday in favour of getIpdStats
export async function getIpdStatsForToday(query: {
  from: string;
  until: string;
}): Promise<ApiResponse<IpdStats>> {
  const response = await Get(`${ipdRoot}/keyStats?from=${query.from}&until=${query.until}`, true);
  return response.data as ApiResponse<IpdStats>;
}

export async function getIpdStats(query: {
  from: string;
  until: string;
  wardType?: TWardType;
}): Promise<ApiResponse<IpdStats>> {
  const params = new URLSearchParams();
  params.append("from", query.from.toString());
  params.append("until", query.until.toString());
  if (query.wardType) params.append("wardType", query.wardType.toString());

  const response = await Get(`${ipdRoot}/keyStats?${params.toString()}`, true);
  return response.data as ApiResponse<IpdStats>;
}

// TODO: change the response format for these later
export const getNextIpdNumber = async (): Promise<NextIpdNumber> => {
  const response = await Get(`${ipdRoot}/getNextIpdNumber`, true);
  return response.data as NextIpdNumber;
};

export async function deleteIpdClient(id: number): Promise<IPD> {
  const response = await Delete(`${ipdRoot}/${id}`, true);
  return response.data as IPD;
}

export async function prescribedMedications(
  id: number,
  medications: Array<IMedicine>
): Promise<IPD> {
  const response = await Patch(`/api/ipd/${id}/prescribedMedication`, medications, true);
  return response.data as IPD;
}

export async function getIpdDetailsByClientId(clientId: number): Promise<IPD> {
  const response = await Get(`${ipdRoot}/getIpdDetailsByClientId/${clientId}`, true);
  return response.data as IPD;
}

export async function getIpdProducts(q: string): Promise<Array<ServiceInterface>> {
  const response = await Get(`${ipdRoot}/search-products?key=${q || ""}`, true);
  return response.data as Array<ServiceInterface>;
}
